import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

interface AuthContextType {
    isAuthenticated: boolean;
    login: (token: string) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true);
    const [token, setToken] = useState<string | null>(null);

    const checkTokenExpiration = (token: string) => {
        try {
            if (token) {
                const decodedToken: any = jwtDecode(token);
                if (decodedToken.exp < Date.now() / 1000) {
                    sessionStorage.removeItem('token');
                    setIsAuthenticated(false);
                    setToken(null);
                } else {
                    setIsAuthenticated(true);
                    setToken(token);
                }
            }
        } catch (error) {
            console.error('Failed to decode token:', error);
            sessionStorage.removeItem('token');
            setIsAuthenticated(false);
            setToken(null);
        }
    };

    useEffect(() => {
        const storedToken = sessionStorage.getItem('token');
        if (storedToken) {
            checkTokenExpiration(storedToken);
            setToken(storedToken);
            setIsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
        }
    }, []);

    const login = (token: string) => {
        setIsAuthenticated(true);
        setToken(token);
        sessionStorage.setItem('token', token);
    };
    const logout = () => {
        setIsAuthenticated(false);
        setToken(null);
        sessionStorage.removeItem('token');
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};